/**
 * Error Logger Utility
 * ====================
 * Frontend'de oluşan hataları backend'e loglayan utility.
 *
 * Usage:
 * import { logErrorToBackend, initErrorLogger } from '@/shared/utils/errorLogger';
 *
 * // Manual logging
 * logErrorToBackend({
 *   errorType: 'API Error',
 *   errorMessage: 'Failed to fetch data',
 *   severity: 'ERROR'
 * });
 *
 * // Global error tracking (call once in app initialization)
 * initErrorLogger();
 */

import axios from 'axios';
import type { AxiosError } from 'axios';
import type { BrowserInfo, Severity } from '@/shared/types/observability';
import { supabase } from './supabaseClient';
import { getAccessToken } from './authSession';

export interface ErrorLogInput {
  errorType: string;
  errorMessage: string;
  errorStack?: string | null;
  componentStack?: string | null;
  severity?: Severity;
  metadata?: Record<string, unknown>;
}

interface Breadcrumb {
  t: string;
  category: string;
  message: string;
}

interface ErrorLoggerWindow extends Window {
  __ERROR_LOGGER_INITIALIZED__?: boolean;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

// Backend /api/error-logs ERROR_LOG_CAPS ile BİREBİR — uzun gerçek hata 400 ile reddedilip KAYBOLMASIN (review #17/#26)
const FE_CAPS = { errorType: 200, errorMessage: 4000, errorStack: 16000, componentStack: 16000, url: 2000, pathname: 1000, userAgent: 1000, metadataBytes: 8000 };
const _cap = (s: string | null | undefined, n: number): string | null | undefined => (typeof s === 'string' && s.length > n ? s.slice(0, n) : s);

// ─── Breadcrumbs (Sentry auto-breadcrumb paritesi) ──────────────────────────
// "Kullanıcı bu hataya NASIL geldi" izi: son N aksiyon (route, tıklama, API).
// Ring buffer (max 15). Her hata raporunun metadata'sına eklenir. KVKK: serbest
// metin (textContent) YOK — sadece tag + aria-label/title gibi aksiyon etiketi;
// PII sızmaz. Backend metadata 8KB cap'i için lean tutuldu (kısa mesaj, data yok).
const BREADCRUMB_MAX = 15;
const _breadcrumbs: Breadcrumb[] = [];
export function addBreadcrumb(category: string, message: unknown): void {
  try {
    _breadcrumbs.push({
      t: new Date().toISOString(),
      category: String(category || 'manual').slice(0, 24),
      message: String(message == null ? '' : message).slice(0, 120),
    });
    if (_breadcrumbs.length > BREADCRUMB_MAX) _breadcrumbs.shift();
  } catch {
    /* breadcrumb ASLA uygulama akışını bozmaz */
  }
}
function getBreadcrumbs(): Breadcrumb[] {
  return _breadcrumbs.slice();
}

/**
 * Get browser information
 */
function getBrowserInfo(): BrowserInfo | null {
  if (typeof window === 'undefined') return null;

  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  // Browser detection
  if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
    browser = 'Safari';
    version = ua.match(/Version\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Firefox') > -1) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/([\d.]+)/)?.[1] || 'Unknown';
  } else if (ua.indexOf('Edg') > -1) {
    browser = 'Edge';
    version = ua.match(/Edg\/([\d.]+)/)?.[1] || 'Unknown';
  }

  // OS detection
  if (ua.indexOf('Win') > -1) os = 'Windows';
  else if (ua.indexOf('Mac') > -1) os = 'MacOS';
  else if (ua.indexOf('Linux') > -1) os = 'Linux';
  else if (ua.indexOf('Android') > -1) os = 'Android';
  else if (ua.indexOf('iOS') > -1) os = 'iOS';

  // Device detection
  if (/Mobile|Android|iPhone|iPad/.test(ua)) {
    device = 'Mobile';
  } else if (/Tablet|iPad/.test(ua)) {
    device = 'Tablet';
  }

  return { browser, version, os, device };
}

/**
 * Log error to backend
 * @param {Object} errorData - Error information
 * @param {string} errorData.errorType - Type of error
 * @param {string} errorData.errorMessage - Error message
 * @param {string} [errorData.errorStack] - Error stack trace
 * @param {string} [errorData.componentStack] - React component stack
 * @param {string} [errorData.severity='ERROR'] - Error severity (INFO, WARNING, ERROR, FATAL)
 * @param {Object} [errorData.metadata] - Additional metadata
 */
export async function logErrorToBackend(errorData: ErrorLogInput): Promise<void> {
  try {
    // Don't log in development if disabled
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DISABLE_ERROR_LOGGING === 'true') {
      console.warn('[Error Logger] Disabled in development');
      return;
    }

    // Sentry best-practice: lokal/dev ortamından prod ErrorLog'a GÖNDERME (dev frontend prod backend'e
    // yazıyordu → 'Failed to read source code' compile/HMR gürültüsünün kaynağı). Localhost = dev,
    // gerçek domain = production. Override: NEXT_PUBLIC_ERRORLOG_FORCE=true.
    if (typeof window !== 'undefined'
        && /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname)
        && process.env.NEXT_PUBLIC_ERRORLOG_FORCE !== 'true') {
      return;
    }

    const {
      errorType,
      errorMessage,
      errorStack,
      componentStack,
      severity = 'ERROR',
      metadata = {}
    } = errorData;

    // Auth token — authSession SSoT (senkron cache, onAuthStateChange ile güncel). Cache erken-render
    // race'ini kapatır → hata anında token ANINDA hazır → backend optional-auth userId'yi doldurur (anon
    // kalmaz). Cache boşsa (init öncesi nadir çağrı) getSession fallback. Eskiden HER çağrı getSession await
    // ediyordu → erken hata token'sız → userId=anon (SablonKapak). SSoT tek token kaynağı = drift yok.
    let authToken = getAccessToken();
    if (!authToken) {
      try {
        const { data } = await supabase.auth.getSession();
        authToken = data?.session?.access_token || null;
      } catch { /* token yoksa anonim logla */ }
    }

    // Prepare request data — alanlar backend cap'lerine truncate edilir (400-reddi → kayıp önle)
    const requestData: {
      errorType: string;
      errorMessage: string;
      errorStack: string | null | undefined;
      componentStack: string | null | undefined;
      url: string;
      pathname: string;
      userAgent: string;
      severity: Severity;
      browserInfo: BrowserInfo | null;
      metadata: Record<string, unknown>;
    } = {
      errorType: _cap(errorType, FE_CAPS.errorType),
      errorMessage: _cap(errorMessage, FE_CAPS.errorMessage),
      errorStack: _cap(errorStack, FE_CAPS.errorStack),
      componentStack: _cap(componentStack, FE_CAPS.componentStack),
      url: _cap(typeof window !== 'undefined' ? window.location.href : 'SSR', FE_CAPS.url),
      pathname: _cap(typeof window !== 'undefined' ? window.location.pathname : 'SSR', FE_CAPS.pathname),
      userAgent: _cap(typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown', FE_CAPS.userAgent),
      severity,
      browserInfo: getBrowserInfo(),
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
        // Release parity (backend logger.release ile aynı amaç): frontend hatası hangi deploy'da çıktı.
        release: (process.env.NEXT_PUBLIC_COMMIT_SHA || 'web').slice(0, 12),
        breadcrumbs: getBreadcrumbs(),
        screenResolution: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : null
      }
    };

    // metadata 8KB cap (backend ERROR_LOG_OBJ_BYTES) — aşarsa kademeli kıs (breadcrumb → responseData → minimal)
    try {
      if (JSON.stringify(requestData.metadata).length > FE_CAPS.metadataBytes) {
        requestData.metadata = { ...requestData.metadata, breadcrumbs: getBreadcrumbs().slice(-5), responseData: undefined, _trimmed: true };
        if (JSON.stringify(requestData.metadata).length > FE_CAPS.metadataBytes) {
          requestData.metadata = { release: requestData.metadata.release, timestamp: requestData.metadata.timestamp, _trimmed: 'oversize' };
        }
      }
    } catch { /* stringify edilemezse dokunma */ }

    // Ali 2026-06-05: 429 / rate-limit hatalarını backend'e POST'LAMA. POST'un
    // kendisi de 429 yer (cascade), VT'yi şişirir, retry fırtınasını büyütür.
    // Bunlar geçici/altyapısal — kalıcı kayıt değeri yok, sessiz geç.
    const _rl = `${errorType} ${errorMessage}`.toLowerCase();
    if (_rl.includes('429') || _rl.includes('too many') || _rl.includes('rate limit')) {
      return;
    }

    // SADECE ağ/bağlantı hatası (axios ERR_NETWORK) — uygulama bug'ı DEĞİL, aksiyon
    // alınamaz. Deploy anında sayfanın tüm paralel çağrıları aynı saniyede ERR_NETWORK
    // atıp ErrorLog'u şişirir. apiErrorLogger:27 ile aynı politika.
    // 🔴 PRECISE — SADECE bu kesin-connectivity kimliği (errorType/mesaj/kod). Gerçek
    //    hataları (TypeError, 5xx, validation, null-ref) ASLA kesmez; navigator.onLine
    //    gibi geniş koşul YOK (offline anındaki gerçek bug'ı yutmasın — Ali 2026-07-01).
    if (
      errorType === 'Network Error' ||
      errorMessage === 'Network Error' ||
      metadata?.code === 'ERR_NETWORK'
    ) {
      return;
    }

    // Send to backend
    await axios.post(`${API_BASE_URL}/api/error-logs`, requestData, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      timeout: 5000 // 5 second timeout
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[Error Logger] Error logged to backend:', errorType, errorMessage);
    }

  } catch (error) {
    // Silently fail - don't throw errors in error logger
    // NOT: Bu mesaj filtreleniyor, döngü oluşturmaz
    console.warn('[Error Logger] Failed to log error to backend:', {
      originalError: errorData.errorType + ': ' + errorData.errorMessage?.substring(0, 100),
      failReason: (error as Error).message
    });
  }
}

/**
 * Initialize global error tracking
 * Call this once in _app.js or index.js
 */
export function initErrorLogger() {
  if (typeof window === 'undefined') return;

  // Prevent duplicate initialization
  if ((window as ErrorLoggerWindow).__ERROR_LOGGER_INITIALIZED__) {
    return;
  }
  (window as ErrorLoggerWindow).__ERROR_LOGGER_INITIALIZED__ = true;

  // Benign hata pattern'leri (Sentry ignoreErrors paritesi) — HEM window.onerror HEM console.error.
  // Next.js router race/cancel + React dev warning'leri gerçek bug değil, gürültü.
  // "Abort fetching component for route" Error OBJESİ olarak console.error'a gelir → her iki yolu da kapsa.
  const IGNORED_CONSOLE_PATTERNS = [
    'Warning:',
    'React does not recognize',
    'DevTools',
    '[Error Logger]',                          // kendi mesajları (döngü önleme)
    'Abort fetching component for route',      // Next.js router race — auth redirect zincirinde benign
    'Loading initial props cancelled',         // Next.js nav cancel
    'Route Cancelled',                         // next/router cancel event
    'Route did not complete loading',          // Next.js nav abort (kullanıcı hızlı geçince/redirect kesince) — benign
    'The user aborted a request',              // AbortController user navigation
    'ResizeObserver loop',                     // tarayıcı benign uyarısı
    'href-interpolation-failed',               // Next.js dinamik-route prefetch interpolasyon hatası (benign — gerçek tık interpolasyonlu href kullanır)
    'is missing query values'                  // aynı hatanın mesaj gövdesi (URL kaçarsa yakala)
  ];
  const isIgnoredConsole = (text: unknown): boolean =>
    typeof text === 'string' && IGNORED_CONSOLE_PATTERNS.some((p) => text.includes(p));

  // Tarayıcı eklentisi kaynaklı hata mı (chrome-extension:// / moz-extension://) — bizim kod
  // DEĞİL (ör. kripto cüzdan eklentisi "Cannot redefine property: ethereum"). ErrorLog gürültüsü.
  const isExtensionSource = (s: unknown): boolean =>
    typeof s === 'string' &&
    (s.includes('chrome-extension://') || s.includes('moz-extension://') || s.includes('safari-extension://'));

  // Chunk yükleme hatası (deploy sonrası bayat client): kullanıcı sayfayı eski deploy'dan açık
  // tutmuşken yeni Vercel deploy'u eski hash'li chunk'ı siler → 404 → "Failed to load script" /
  // "ChunkLoadError". Kod bug'ı DEĞİL. Tek-seferlik auto-reload ile taze deploy'un chunk'ları
  // yüklenir; 10 sn loop-guard ile sonsuz reload önlenir. Reload sonrası hâlâ hata varsa (gerçek
  // 404) loglanır — o zaman false döner ve normal log yoluna düşer.
  // 'attempted to hard navigate to the same URL': aynı sınıf — Next pages-router route değişiminde
  // eski deploy'un _buildManifest'ini yükleyemeyince aynı URL'e hard-navigate dener, sonsuz döngü
  // korumasıyla FIRLATIR (router.js handleHardNavigation) → sayfa eski kodla kalır. 4 kayıt
  // (2026-10-16/17, her biri farklı release, sık deploy günleri) — tek-seferlik reload düzeltir.
  const CHUNK_ERROR_PATTERNS = ['ChunkLoadError', 'Loading chunk', 'Failed to load script', 'Importing a module script', 'attempted to hard navigate to the same URL'];
  const handleChunkError = (s: unknown): boolean => {
    if (typeof s !== 'string' || !CHUNK_ERROR_PATTERNS.some((p) => s.includes(p))) return false;
    try {
      const KEY = '__chunk_reload_ts__';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last < 10000) return false; // yakında zaten yenilendi → hâlâ hata = gerçek sorun, logla
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
      return true; // reload tetiklendi → logu atla
    } catch {
      window.location.reload();
      return true;
    }
  };

  // Global error handler
  window.addEventListener('error', (event) => {
    const msg = event.message || '';

    // Chunk yükleme hatası → tek-seferlik reload (deploy bayat-client fix), log atla
    if (handleChunkError(msg)) return;

    // "Script error." = cross-origin third-party script hatası, detay yok, loglamak anlamsız
    if (msg === 'Script error.' || msg === 'Script error') return;
    // Benign Next.js router/dev hataları — yakalama (gürültü)
    if (isIgnoredConsole(msg)) return;
    // Tarayıcı eklentisi (cüzdan vb.) hatası — bizim kod değil, ErrorLog'a yazma
    if (isExtensionSource(event.filename) || isExtensionSource(event.error?.stack)) return;

    logErrorToBackend({
      errorType: 'JavaScript Error',
      errorMessage: msg || 'Unknown error',
      errorStack: event.error?.stack || null,
      severity: 'ERROR',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  // Unhandled promise rejection handler
  //
  // Backend'e göndermeden önce known-noise pattern'leri filtrele. Bu hatalar
  // genellikle:
  //   - PWA Service Worker / Workbox runtime caching (3rd-party 4xx/CORS fail)
  //   - Tarayıcı extension'ları (cross-origin scripts)
  //   - Network kesintileri (kullanıcı tarafı)
  // Backend ErrorLog tablosunu flood ediyor ve gerçek bug'ları örtüyor —
  // signal-to-noise için listeyi geniş tut. Yeni gürültü çıkarsa buraya ekle.
  const IGNORED_REJECTION_PATTERNS = [
    'ServiceWorker',
    'service worker',
    'sw.js',
    'serviceWorker.register',                    // SW register() reddi — bot/headless SW desteklemez (msg genelde generic "Rejected", imza STACK'te)
    'wrsParams',                                 // Google Web Rendering Service (Googlebot render) — SW register reddi gürültüsü, gerçek kullanıcı değil
    "reading 'waiting'",                          // next-pwa SW-register kodu bot/headless'te registration.waiting'i undefined okuyor (Chrome-headless/Googlebot) — benign, bizim promise-kodumuz değil
    "Failed to execute 'add' on 'Cache'",       // Workbox precache fail (3rd-party fetch reject)
    "Failed to execute 'addAll' on 'Cache'",
    "Failed to execute 'put' on 'Cache'",
    'no-response',                               // Workbox NetworkOnly fallback miss
    'non-precached-url',                         // Workbox precache miss
    'NetworkError when attempting',              // Firefox network fail
    'Load failed',                               // Safari network fail
    'Failed to fetch',                           // Chrome/Edge/Chromium network fail (3rd-party beacon/gtag/analytics pushState-fetch, adblocker/tracking-prevention; Firefox/Safari eşdeğerleri zaten yukarıda — cross-browser parite)
    'The user aborted a request',                // AbortController kullanıcı navigation
    'href-interpolation-failed',                 // Next.js dinamik-route prefetch interpolasyon hatası (benign)
    'is missing query values',                   // aynı hata (mesaj gövdesi — URL kaçarsa yakala)
    'route-guard:',                              // "kaydedilmemiş değişiklik" guard'ı navigasyonu iptal için BİLİNÇLİ throw eder (Next.js pages-router deseni; hata değil)
    'Object Not Found Matching Id',              // Tarayıcı eklentisi (Outlook/Office/email-tarayıcı content-script) RPC gürültüsü — "…Matching Id:N, MethodName:update, ParamCount:4"; STACK'SİZ gelir (extension-source yakalayamaz), bizim promise-kodumuz değil. /auth/login'de 301/dk flood etti (2026-09-02).
    'Lock broken by another request',            // Web Locks API (navigator.locks / Supabase auth multi-tab steal) abort — normal tarayıcı lock geçişi, uygulama hatası değil.
    'was released because another request stole it', // Aynı Supabase auth lock'unun acquire-timeout varyantı ({isAcquireTimeout:true}) — "Lock \"lock:sb-…-auth-token\" was released…"; oturum bir sonraki istekte alınır, kullanıcıya etkisi yok (2026-10-11, 3 kayıt).
  ];

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason?.message || (typeof reason === 'string' ? reason : '') || 'Promise rejected';

    if (handleChunkError(msg)) return; // chunk load fail (dinamik import reject) → tek-seferlik reload
    // msg + stack İKİSİNDE de ara: bazı reddetmelerin mesajı generic ("Rejected"), gürültü
    // imzası yalnız stack'te olur (örn. Googlebot WRS serviceWorker.register → msg "Rejected").
    const rejStack = reason?.stack || '';
    if (IGNORED_REJECTION_PATTERNS.some((p) => msg.includes(p) || rejStack.includes(p))) return;
    // Tarayıcı eklentisi kaynaklı reddetme — bizim kod değil (gürültü)
    if (isExtensionSource(reason?.stack)) return;

    logErrorToBackend({
      errorType: 'Unhandled Promise Rejection',
      errorMessage: msg,
      errorStack: reason?.stack || null,
      severity: 'ERROR',
      metadata: {
        reason: typeof reason === 'object' ? JSON.stringify(reason) : reason
      }
    });
  });

  // Console error override (optional - captures console.error calls)
  const originalConsoleError = console.error;
  console.error = function(...args: unknown[]) {
    // Call original console.error
    originalConsoleError.apply(console, args);

    // Log to backend (only for Error objects or strings)
    const firstArg = args[0];
    if (!(firstArg instanceof Error) && typeof firstArg !== 'string') return; // yalnız Error/string logla

    // 🔴 Noise-filtre TÜM arg'lara bakar (2026-09-05, ErrorLog: sablonPdfCache ChunkLoadError kaçağı):
    //    'prefix' + errorObj deseni yaygın — örn console.error('[x] başarısız:', chunkErr) → ChunkLoadError
    //    2. arg'da olur; sadece firstArg'a bakınca hem chunk-reload tetiklenmiyor hem gürültü loglanıyordu.
    const birlesik = args.map((a) => (a instanceof Error ? `${a.message} ${a.stack || ''}` : String(a))).join(' ');
    if (handleChunkError(birlesik)) return; // chunk load fail (herhangi arg'da) → tek-seferlik reload
    if (isIgnoredConsole(birlesik)) return; // benign (herhangi arg'da)

    if (firstArg instanceof Error) {
      logErrorToBackend({
        errorType: 'Console Error',
        errorMessage: firstArg.message,
        errorStack: firstArg.stack,
        severity: 'WARNING',
        metadata: {
          consoleArgs: args.length > 1 ? args.slice(1).map(a => String(a)) : []
        }
      });
    } else {
      logErrorToBackend({
        errorType: 'Console Error',
        errorMessage: firstArg as string,
        severity: 'WARNING',
        metadata: {
          consoleArgs: args.slice(1).map(a => String(a))
        }
      });
    }
  };

  // ─── Otomatik breadcrumb yakalama (Sentry auto-instrument paritesi) ───────
  // Tıklama: tag + aksiyon etiketi (aria-label/title) — serbest metin YOK (KVKK).
  document.addEventListener(
    'click',
    (e) => {
      try {
        const el = e.target as Element | null;
        if (!el || !el.tagName) return;
        const tag = el.tagName.toLowerCase();
        const label = (
          el.getAttribute?.('aria-label') ||
          el.getAttribute?.('title') ||
          el.getAttribute?.('data-testid') ||
          ''
        )
          .trim()
          .slice(0, 60);
        addBreadcrumb('ui.click', label ? `${tag}[${label}]` : tag);
      } catch {
        /* sessiz */
      }
    },
    true
  );

  // Navigasyon: pushState patch + popstate (route değişimi izi).
  try {
    const _origPush = window.history.pushState;
    window.history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
      try {
        addBreadcrumb('navigation', String(args[2] || window.location.pathname).slice(0, 120));
      } catch {
        /* sessiz */
      }
      return _origPush.apply(this, args);
    };
    window.addEventListener('popstate', () => {
      try {
        addBreadcrumb('navigation', window.location.pathname);
      } catch {
        /* sessiz */
      }
    });
  } catch {
    /* history patch başarısız → breadcrumb'sız devam, akış bozulmaz */
  }

  // Init log kaldırıldı (dev console gürültüsü) — error tracking sessiz çalışır.
}

/**
 * Log API error
 * @param {Object} error - Axios error object
 * @param {string} endpoint - API endpoint
 */
export function logAPIError(err: unknown, endpoint: string): void {
  const error = err as AxiosError<{ error?: string; message?: string; code?: string }>;
  // Planlı bakım 503'ü (backend maintenanceMiddleware) hata değil → ErrorLog'a yazma
  if (error.response?.status === 503 && error.response?.data?.code === 'MAINTENANCE_MODE') return;
  logErrorToBackend({
    errorType: 'API Error',
    errorMessage:
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'API request failed',
    errorStack: error.stack,
    severity: error.response?.status >= 500 ? 'ERROR' : 'WARNING',
    metadata: {
      endpoint,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data
    }
  });
}

/**
 * Log network error
 * @param {Object} error - Network error
 *
 * severity = WARNING (ERROR değil): network error istemci bağlantısı (offline/mobil
 * blip/uyku-polling), uygulama bug'ı DEĞİL → ERROR görünümünü kirletmesin, gerçek
 * bug'ları gizlemesin. Gerçek outage = çok sayıda WARNING spike (proaktif sinyal yaşar).
 * (2026-06-23 — saha/mobil ERR_NETWORK gürültüsü; logApiError zaten tam filtreliyordu.)
 */
export function logNetworkError(error: AxiosError): void {
  logErrorToBackend({
    errorType: 'Network Error',
    errorMessage: error.message || 'Network request failed',
    errorStack: error.stack,
    severity: 'WARNING',
    metadata: {
      code: error.code,
      config: error.config ? {
        url: error.config.url,
        method: error.config.method
      } : null
    }
  });
}
